import { Button } from "@/components/ui/button";
import { ContentLoader } from "@/components/ui/loaders";
import { ActionBar } from "@/components/layout/app.layout";
import {
  Callout,
  DateField,
  TextAreaField,
  TextField,
  Toggle,
} from "@/components/fornada/controls";
import { ConfirmSheet } from "@/components/fornada/sheet";
import { ScreenTitle, Surface } from "@/components/fornada/primitives";
import {
  createSaleSchema,
  updateSaleSchema,
} from "@/api/schemas/sale.schema";
import { useActiveSale } from "@/hooks/use-active-sale";
import { useCreateSale } from "@/hooks/use-create-sale";
import { useUpdateSale } from "@/hooks/use-update-sale";
import { date } from "@/lib/date";
import { ORDER_CLOSE_DAYS_BEFORE, ORDER_CLOSE_HOUR } from "@/lib/sale";
import dayjs from "dayjs";
import { useAuth } from "@/providers/auth.provider";
import { useToast } from "@/providers/toast.provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

type FormValues = {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
};

const DAY = "YYYY-MM-DD";

const CampaignEditPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toaster } = useToast();
  const [params] = useSearchParams();
  const isNew = params.get("new") === "1";
  const { data: sale, isLoading } = useActiveSale();
  const create = useCreateSale();
  const update = useUpdateSale();
  const [closing, setClosing] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(
      isNew ? createSaleSchema : updateSaleSchema,
    ) as unknown as Resolver<FormValues>,
    defaultValues: {
      name: "",
      description: "",
      startDate: date().format(DAY),
      endDate: "",
      isActive: true,
    },
  });

  useEffect(() => {
    if (!isNew && sale) {
      form.reset({
        name: sale.name,
        description: sale.description,
        startDate: date(sale.startDate as unknown as string).format(DAY),
        endDate: date(sale.endDate).format(DAY),
        isActive: sale.isActive,
      });
    }
  }, [isNew, sale]);

  const endDate = form.watch("endDate");
  // Data pura do input (sem fuso): evita o deslocamento de um dia do `date()` em UTC.
  const closeLabel = endDate
    ? dayjs(endDate).subtract(ORDER_CLOSE_DAYS_BEFORE, "day").format("DD/MM/YYYY")
    : null;

  if (isLoading) return <ContentLoader show />;
  if (user?.role !== "ADMIN") return <Navigate to="/" replace />;
  if (!isNew && !sale) return <Navigate to="/campaign/edit?new=1" replace />;

  const pending = create.isPending || update.isPending;

  const submit = form.handleSubmit((values) => {
    if (isNew) {
      create.mutate(values as never, {
        onSuccess: () => {
          toaster("Campanha criada", "success");
          navigate("/", { replace: true });
        },
      });
    } else if (sale) {
      update.mutate(
        { id: sale.id, ...values } as never,
        {
          onSuccess: () => {
            toaster("Campanha atualizada", "success");
            navigate("/campaign", { replace: true });
          },
        },
      );
    }
  });

  const closeCampaign = () => {
    if (!sale) return;
    update.mutate(
      { id: sale.id, ...form.getValues(), isActive: false } as never,
      {
        onSuccess: () => {
          setClosing(false);
          toaster("Campanha encerrada", "success");
          navigate("/", { replace: true });
        },
        onSettled: () => setClosing(false),
      },
    );
  };

  return (
    <form onSubmit={submit} className="mx-auto flex w-full max-w-xl flex-col gap-6">
      <Link
        to={isNew ? "/" : "/campaign"}
        className="-mb-2 flex size-11 items-center justify-center rounded-md text-fx-body hover:bg-fx-raised min-[960px]:hidden"
        aria-label="Voltar"
      >
        <ArrowLeft size={22} strokeWidth={1.6} />
      </Link>

      <ScreenTitle
        eyebrow={isNew ? "Nova campanha" : "Editar campanha"}
        title={isNew ? "Criar campanha" : "Editar campanha"}
        subtitle="Os vendedores veem a campanha ativa no Início."
      />

      <div className="flex flex-col gap-3">
        <Controller
          name="name"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Nome"
              placeholder="Ex: Campanha de Outubro"
              autoComplete="off"
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="description"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextAreaField
              {...field}
              label="Descrição"
              placeholder="Ex: Pizzas congeladas. Retirada no Salão Paroquial."
              error={fieldState.error?.message}
            />
          )}
        />
        <div className="grid grid-cols-2 gap-3">
          <Controller
            name="startDate"
            control={form.control}
            render={({ field, fieldState }) => (
              <DateField {...field} label="Início" error={fieldState.error?.message} />
            )}
          />
          <Controller
            name="endDate"
            control={form.control}
            render={({ field, fieldState }) => (
              <DateField {...field} label="Entrega" error={fieldState.error?.message} />
            )}
          />
        </div>
      </div>

      {closeLabel && (
        <Callout icon={<Clock size={18} strokeWidth={1.6} />}>
          Pedidos até{" "}
          <strong className="font-bold text-fx-title">{closeLabel}</strong>, às {ORDER_CLOSE_HOUR}h
          (4,5 dias antes do fim do dia da entrega).
        </Callout>
      )}

      <Surface className="px-4">
        <Controller
          name="isActive"
          control={form.control}
          render={({ field }) => (
            <Toggle
              checked={field.value}
              onChange={field.onChange}
              label="Campanha ativa"
              description="Aparece no Início de todos os vendedores"
            />
          )}
        />
      </Surface>

      {!isNew && (
        <button
          type="button"
          onClick={() => setClosing(true)}
          className="flex h-11 items-center self-start text-sm font-semibold text-fx-danger-ink hover:underline"
        >
          Encerrar campanha
        </button>
      )}

      <ActionBar className="min-[960px]:justify-end">
        <Button
          asChild
          variant="quiet"
          size="lg"
          className="w-[120px]"
        >
          <Link to={isNew ? "/" : "/campaign"}>Cancelar</Link>
        </Button>
        <Button type="submit" size="lg" className="flex-1 min-[960px]:flex-none" disabled={pending}>
          {isNew ? "Criar campanha" : "Salvar alterações"}
        </Button>
      </ActionBar>

      <ConfirmSheet
        open={closing}
        onOpenChange={setClosing}
        title="Encerrar campanha?"
        description="Ela deixa de aparecer no Início e os vendedores não conseguem mais registrar pedidos. Os pedidos já feitos continuam salvos."
        confirmLabel="Encerrar campanha"
        destructive
        pending={pending}
        onConfirm={closeCampaign}
      />
    </form>
  );
};

export default CampaignEditPage;
